import React, { useCallback, useEffect, useState, useRef } from 'react';
import type { ModalControls } from 'hooks/useModalControls';
import { Modal } from 'components/modal/Modal';
import { Btn } from 'components/btn/Btn';
import { DatePicker } from 'components/date-picker';
import MaskedInput, { MaskedInputProps } from 'react-text-mask';
import { createStyles } from 'utils/createStyle';
import { MASK_DATE } from 'common/modules/mask-text/masks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { faUmbrellaBeach } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';

import { daysFromNow } from 'common/utils/time';
import { useVacationModeUpdateCtrl, OrgOptionsWithDates } from './utils';

const MAX_START_DATE = daysFromNow(364);
const MAX_END_DATE = daysFromNow(365);

const styles = createStyles({
    dataPickerInput: [
        'w-11/12 outline-none text-grey-1',
        {
            fontSize: '16px',
            caretColor: 'transparent',
        },
    ],
    dataPickerInputWrapper: [
        'flex items-center text-left pl-4 pr-2 border border-grey-light rounded-lg',
        {
            width: '236px',
            height: 48,
            outline: 'none',
        },
    ],
});

type CustomMaskedDateInputProps = Omit<MaskedInputProps, 'mask'> & {
    isOpen: boolean;
    toggle: (value: boolean) => void;
};

const CustomMaskedDateInput = ({ isOpen, toggle, value, ...props }: CustomMaskedDateInputProps) => {
    const inputRef = useRef<MaskedInput>(null);

    const onCalendarClick = () => {
        inputRef.current?.inputElement.focus();
    };
    return (
        <div className={styles.dataPickerInputWrapper}>
            <MaskedInput
                readOnly
                value={value}
                {...props}
                mask={MASK_DATE}
                className={styles.dataPickerInput}
                placeholder="MM/DD/YYYY"
                onKeyDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }}
                onPaste={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    return false;
                }}
                ref={inputRef}
            />
            <div
                onClick={() => onCalendarClick()}
                className={`text-center focus:outline-none  px-2 pb-0.5 rounded-full ${
                    isOpen ? 'bg-pale-blue' : 'bg-white'
                }`}
            >
                <FontAwesomeIcon icon={faCalendar} size="sm" className="text-brand" />
            </div>
        </div>
    );
};

interface VacationModeModalProps {
    modalControls: ModalControls;
    vacationModeState: OrgOptionsWithDates | null;
    isVacationModeScheduled: boolean;
}

export const VacationModeModal = ({
    modalControls,
    vacationModeState,
    isVacationModeScheduled,
}: VacationModeModalProps) => {
    const { vacationModeStartDate, vacationModeEndDate, isVacationModeActive } =
        vacationModeState || {};
    const [startDate, setStartDate] = useState(
        vacationModeStartDate ? vacationModeStartDate : null
    );
    const [endDate, setEndDate] = useState(vacationModeEndDate ? vacationModeEndDate : null);
    const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
    const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
    const { updateVacationModeData, hasError } = useVacationModeUpdateCtrl();
    const [errorStartDate, setErrorStartDate] = useState('');
    const [errorEndDate, setErrorEndDate] = useState('');
    const [generalError, setGeneralError] = useState('');

    useEffect(() => {
        vacationModeStartDate ? setStartDate(vacationModeStartDate) : null;
        vacationModeEndDate ? setEndDate(vacationModeEndDate) : null;
        setErrorEndDate(' ');
        setErrorStartDate(' ');
        setGeneralError('');
        setIsEndDatePickerOpen(false);
        setIsStartDatePickerOpen(false);
    }, [modalControls.isVisible]);

    useEffect(() => {
        vacationModeStartDate ? setStartDate(vacationModeStartDate) : null;
    }, [vacationModeStartDate]);

    useEffect(() => {
        vacationModeEndDate ? setEndDate(vacationModeEndDate) : null;
    }, [vacationModeEndDate]);

    const onStartDateChange = useCallback(
        (date: Date) => {
            setStartDate(date);
            setIsStartDatePickerOpen(false);
            setErrorStartDate('');
        },
        [setStartDate]
    );

    const onEndDateChange = useCallback(
        (date: Date) => {
            setEndDate(date);
            setIsEndDatePickerOpen(false);
            setErrorEndDate('');
        },
        [setEndDate]
    );

    const onCloseModal = useCallback(() => {
        modalControls.toggleModalVisibility();
        setStartDate(null);
        setEndDate(null);
        setErrorStartDate('');
        setErrorEndDate('');
        setGeneralError('');
    }, [modalControls]);

    const onSaveModal = useCallback(async () => {
        if (!startDate || !endDate) {
            !startDate && setErrorStartDate('Start Date is required.');
            !endDate && setErrorEndDate('End Date is required.');
            return;
        }

        const success = await updateVacationModeData({
            vacationModeStartDate: startDate.toISOString(),
            vacationModeEndDate: endDate.toISOString(),
        });

        if (!success) {
            setGeneralError(
                'There was an error saving your Vacation Mode. Please try again or call Customer Support at 800-727-4715 (option 3) for assistance.'
            );
            return;
        }
        modalControls.toggleModalVisibility();
    }, [startDate, endDate, updateVacationModeData, modalControls]);

    const onCancelVacationMode = useCallback(async () => {
        const success = await updateVacationModeData({
            vacationModeEndDate: null,
            vacationModeStartDate: null,
            removeVacationMode: true,
        });
        if (!success) {
            setGeneralError(
                'There was an error canceling your Vacation Mode. Please try again or call Customer Support at 800-727-4715 (option 3) for assistance.'
            );
            return;
        }
        setStartDate(null);
        setEndDate(null);
        modalControls.toggleModalVisibility();
    }, [updateVacationModeData, modalControls]);

    return (
        <Modal title="" isOpen={modalControls.isVisible} onClose={onCloseModal}>
            <div className="flex flex-col items-center mb-8">
                <FontAwesomeIcon
                    icon={faUmbrellaBeach}
                    style={{ width: '50px', height: '50px' }}
                    color="#3EB5E5"
                />
            </div>
            <h5 className="text-center text-29px font-semibold mb-3 sm:mb-6">Vacation Mode</h5>
            <p className="text-grey-1 text-base  text-center px-4">
                When Vacation Mode is on, participants will not be able to use 'Pay Coordinator' or
                'Ship to Coordinator'. Simply set your desired start and end dates then relax,
                knowing that fundraising will continue while you're away.
            </p>
            <div className="flex flex-col sm:flex-row items-center mt-4 sm:mt-8 justify-between mx-3">
                <div id="dt-start">
                    <div className="text-sm text-left text-grey-1 font-semibold mb-2">
                        Start Date
                    </div>
                    <DatePicker
                        selected={startDate || null}
                        onChange={onStartDateChange}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        minDate={new Date()}
                        maxDate={endDate || MAX_START_DATE}
                        customInput={
                            <CustomMaskedDateInput
                                toggle={setIsStartDatePickerOpen}
                                isOpen={isStartDatePickerOpen}
                            />
                        }
                        open={isStartDatePickerOpen}
                        onCalendarOpen={() => setIsStartDatePickerOpen(true)}
                        onCalendarClose={() => setIsStartDatePickerOpen(false)}
                    />
                    <div className="text-error block text-xs text-left mt-0 sm:mt-2 h-5">
                        {errorStartDate}
                    </div>
                </div>
                <div id="dt-end">
                    <div className="text-sm text-left text-grey-1 font-semibold mb-2">End Date</div>
                    <DatePicker
                        selected={endDate || null}
                        onChange={onEndDateChange}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate || daysFromNow(1)}
                        maxDate={MAX_END_DATE}
                        customInput={
                            <CustomMaskedDateInput
                                toggle={setIsEndDatePickerOpen}
                                isOpen={isEndDatePickerOpen}
                            />
                        }
                        open={isEndDatePickerOpen}
                        onCalendarOpen={() => setIsEndDatePickerOpen(true)}
                        onCalendarClose={() => setIsEndDatePickerOpen(false)}
                    />
                    <div className="text-error text-xs text-left mt-0 sm:mt-2 h-5">
                        {errorEndDate}
                    </div>
                </div>
            </div>
            {hasError && generalError ? (
                <div className="text-error flex text-sm text-left mx-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 mt-1" />
                    <span className="leading-6">{generalError}</span>
                </div>
            ) : null}
            <div className="flex flex-col gap-4 my-4 sm:my-8 mx-auto">
                <Btn className="text-lg" onClick={() => onSaveModal()}>
                    Save {isVacationModeActive || isVacationModeScheduled ? 'Changes' : ''}
                </Btn>
                {isVacationModeActive || isVacationModeScheduled ? (
                    <Btn isSecondary className="text-sm" onClick={() => onCancelVacationMode()}>
                        Cancel Vacation Mode
                    </Btn>
                ) : null}
                <button
                    type="button"
                    className=" text-brand font-semibold text-lg"
                    onClick={onCloseModal}
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default VacationModeModal;
